import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { kebabCase } from '@makemydeal/dr-common-utils';
import { setFieldError } from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import { updateBalloonType } from '@makemydeal/dr-dash-store/actions/offerRedux';
import { getIsUserProgramQuotes, getBalloonType } from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { wasTheLastFailure } from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import { UPDATED_BALLOON_TYPE } from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import { getCurrentDealMode } from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import { DealMode } from '@makemydeal/dr-dash-store/types/dealModeEnum';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DealTabs } from '@makemydeal/dr-shared-types';

import { dealLockHooks } from '../../../hooks';
import { BALLOON_TERM_TYPE, GENERIC_LAST_FIELD_CHANGED_ERROR } from '../../../constants';

const BALLOON_TERM_TYPE_OPTIONS = [
    { id: 'term', label: 'Term', value: 'term' },
    { id: 'term-1', label: 'Term-1', value: 'term-1' }
];

export const useBalloonTermTypeField = () => {
    const dispatch = useDispatch();
    const fieldKey = kebabCase(BALLOON_TERM_TYPE);

    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isUserProgramQuotes = useSelector(getIsUserProgramQuotes);
    const dealMode = useSelector(getCurrentDealMode);
    const isBaseDealScreenExperience = useSelector(dealerSelectors.isBaseDealExperience);
    const isLocked = dealLockHooks.useIsDealLocked();

    const balloonType = useSelector(getBalloonType);

    const failed = useSelector(wasTheLastFailure(UPDATED_BALLOON_TYPE));
    const failedMessage = useMemo(() => (failed ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined), [failed]);

    const handleValueChange = useCallback(
        (event: any) => {
            const selectedType = event.target.value;

            if (selectedType) {
                dispatch(updateBalloonType(selectedType, true));
            }
        },
        [dispatch]
    );

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled) {
            dispatch(setFieldError(DealTabs.SUMMARY, fieldKey, failed));
        }
    }, [isDealScreenRedDotErrorsEnabled, dispatch, failed, fieldKey]);

    // Display Rule 1 (PQ-Desking Mode): Disable when in Manager View desking mode with PQ (Priced Quote)
    // Note: PQ = !isUserProgramQuotes (UPQ = isUserProgramQuotes)
    const isDisabledByPQDeskingMode = dealMode === DealMode.deskingMode && !isUserProgramQuotes;

    // Display Rule 2 (UPQ-F&I Mode): Enable when in Base Deal Screen with UPQ or F&I mode
    const isEnabledByUPQOrFnIMode = (isBaseDealScreenExperience && isUserProgramQuotes) || dealMode === DealMode.fiMode;

    const isDisabled = isLocked || (isDisabledByPQDeskingMode && !isEnabledByUPQOrFnIMode);

    return {
        label: BALLOON_TERM_TYPE,
        selectProps: {
            name: fieldKey,
            onChange: handleValueChange,
            value: balloonType?.toLocaleLowerCase?.(),
            options: BALLOON_TERM_TYPE_OPTIONS,
            errorMessage: failedMessage,
            hasError: failedMessage ? true : false,
            displayDeselectOption: false,
            displayLabel: false,
            'data-testid': fieldKey,
            size: 'sm' as const,
            disabled: isDisabled
        }
    };
};
